import { Select, Spin } from "antd";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import toast from "react-hot-toast";
import { Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Row } from "reactstrap";
import { getAllManufacturers } from "../../api/pagination";
import { saveData, uniqueID } from "../../firebase/utility";
import GeneralButton from "../button/GeneralButton";

const AllowedManufacturerModal = ({ modal, toggle, selectedItem }) => {
    const [formData, setFormData] = useState({
        dnhVendorName: "",
        enabled: true,
        manufacturerId: null,
        markupPercent: 0,
        syncToAlgolia: true
    });
    const [loading, setLoading] = useState(false);
    const [manufacturers, setManufacturers] = useState([]);
    const [mfgLoading, setMfgLoading] = useState(false);

    const fetchManufacturersDebounced = useCallback(
        debounce((searchText) => {
            getAllManufacturers(1, searchText, 50, (res) => {
                if (res?.status === 200) {
                    setManufacturers(res?.data?.data || []);
                }
                setMfgLoading(false);
            });
        }, 500),
        []
    );

    const handleSearchManufacturers = (searchText) => {
        setMfgLoading(true);
        fetchManufacturersDebounced(searchText);
    };

    useEffect(() => {
        if (modal) {
            handleSearchManufacturers("");
        }
    }, [modal]);

    useEffect(() => {
        if (selectedItem) {
            setFormData({
                dnhVendorName: selectedItem.dnhVendorName || "",
                enabled: selectedItem.enabled !== undefined ? selectedItem.enabled : true,
                manufacturerId: selectedItem.manufacturerId || null,
                markupPercent: selectedItem.markupPercent || 0,
                syncToAlgolia: selectedItem.syncToAlgolia !== undefined ? selectedItem.syncToAlgolia : true,
                _id: selectedItem._id
            });
        } else {
            setFormData({
                dnhVendorName: "",
                enabled: true,
                manufacturerId: null,
                markupPercent: 0,
                syncToAlgolia: true
            });
        }
    }, [selectedItem, modal]);

    const handleSubmit = async () => {
        if (!formData.dnhVendorName) {
            toast.error("Please enter DNH Vendor Name");
            return;
        }
        if (!formData.manufacturerId) {
            toast.error("Please select a Manufacturer");
            return;
        }

        setLoading(true);
        const _id = formData._id || uniqueID();
        const data = {
            ...formData,
            _id,
            updatedAt: Date.now(),
            createdAt: selectedItem?.createdAt || Date.now()
        };

        const res = await saveData("AllowedManufacturers", _id, data);
        setLoading(false);
        if (res) {
            toast.success("Allowed Manufacturer saved successfully!");
            toggle();
        } else {
            toast.error("Failed to save Allowed Manufacturer");
        }
    };

    return (
        <Modal isOpen={modal} toggle={toggle} centered size="md">
            <ModalHeader toggle={toggle}>
                {selectedItem ? "Edit Allowed Manufacturer" : "Add Allowed Manufacturer"}
            </ModalHeader>
            <ModalBody>
                <Form>
                    <Row>
                        <Col md={12}>
                            <FormGroup>
                                <Label className="fw-bold">DNH Vendor Name</Label>
                                <Input
                                    type="text"
                                    placeholder="Enter DNH Vendor Name"
                                    value={formData.dnhVendorName}
                                    onChange={(e) => setFormData({ ...formData, dnhVendorName: e.target.value })}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={12}>
                            <FormGroup>
                                <Label className="fw-bold">Local Manufacturer</Label>
                                <Select
                                    showSearch
                                    style={{ width: '100%' }}
                                    placeholder="Select Local Manufacturer"
                                    filterOption={false}
                                    onSearch={handleSearchManufacturers}
                                    loading={mfgLoading}
                                    notFoundContent={mfgLoading ? <Spin size="small" /> : null}
                                    value={formData.manufacturerId}
                                    onChange={(val) => setFormData({ ...formData, manufacturerId: val })}
                                    size="large"
                                >
                                    {manufacturers?.map((mfg) => (
                                        <Select.Option key={mfg._id} value={mfg._id}>
                                            {mfg.name}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </FormGroup>
                        </Col>
                        <Col md={12}>
                            <FormGroup>
                                <Label className="fw-bold">Markup Percent (e.g. 0.1 for 10%)</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    placeholder="0.1"
                                    value={formData.markupPercent}
                                    onChange={(e) => setFormData({ ...formData, markupPercent: parseFloat(e.target.value) || 0 })}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup check className="mt-2">
                                <Label check>
                                    <Input
                                        type="checkbox"
                                        checked={formData.enabled}
                                        onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                                    />{' '}
                                    Enabled
                                </Label>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup check className="mt-2">
                                <Label check>
                                    <Input
                                        type="checkbox"
                                        checked={formData.syncToAlgolia}
                                        onChange={(e) => setFormData({ ...formData, syncToAlgolia: e.target.checked })}
                                    />{' '}
                                    Sync to Algolia
                                </Label>
                            </FormGroup>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-4">
                        <GeneralButton
                            title={"Save"}
                            className={"blue-button"}
                            width={"120px"}
                            handleClick={handleSubmit}
                            loading={loading}
                            disabled={loading}
                        />
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default AllowedManufacturerModal;
